export const BILLING_DATA_CLASSIFICATION_PRODUCT_DIVISION = "Product Division";
export const BILLING_DATA_CLASSIFICATION_PRODUCT_CLASS = "Product Class";
export const BILLING_DATA_CLASSIFICATION_PRODUCT_CODE = "Product Code";
export const BILLING_DATA_CLASSIFICATION_PRODUCT_TYPE = "Product Type";
export const BILLING_DATA_CLASSIFICATION_PRODUCTION_LINE = "Production Line";
export const BILLING_DATA_CLASSIFICATION_WAREHOUSE = "Warehouse";

export const BILLING_DATA_CLASSIFICATION_OPTIONS = [
  BILLING_DATA_CLASSIFICATION_PRODUCT_DIVISION,
  BILLING_DATA_CLASSIFICATION_PRODUCT_CLASS,
  BILLING_DATA_CLASSIFICATION_PRODUCT_CODE,
  BILLING_DATA_CLASSIFICATION_PRODUCT_TYPE,
  BILLING_DATA_CLASSIFICATION_PRODUCTION_LINE,
  BILLING_DATA_CLASSIFICATION_WAREHOUSE,
];

export const BILLING_DATA_CLASSIFICATION_NO_DATA_MESSAGE =
  "No Data Found for selected Billing Data Classification. Contact administration.";

function hydrateClassifyFieldsFromValue(classification, value) {
  const v = String(value ?? "").trim();
  const emptyClassifyFields = {
    billingDataClassificationValue: v,
    productClass: "",
    productCode: "",
    productType: "",
    productLine: "",
    warehouseName: "",
  };
  if (!v) {
    return emptyClassifyFields;
  }
  switch (classification) {
    case BILLING_DATA_CLASSIFICATION_PRODUCT_CLASS:
      return { ...emptyClassifyFields, productClass: v };
    case BILLING_DATA_CLASSIFICATION_PRODUCT_CODE:
      return { ...emptyClassifyFields, productCode: v };
    case BILLING_DATA_CLASSIFICATION_PRODUCT_TYPE:
      return { ...emptyClassifyFields, productType: v };
    case BILLING_DATA_CLASSIFICATION_PRODUCTION_LINE:
      return { ...emptyClassifyFields, productLine: v };
    case BILLING_DATA_CLASSIFICATION_WAREHOUSE:
      return { ...emptyClassifyFields, warehouseName: v };
    default:
      return emptyClassifyFields;
  }
}

export function createEmptyBillingClassificationRow(assignedCompanyNumbers = []) {
  const nums = (assignedCompanyNumbers ?? []).filter(Boolean);
  return {
    id: `bc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    companyNumber: nums.length > 0 ? nums[0] : "",
    billingDataClassification: "",
    billingDataClassificationValue: "",
    productClass: "",
    productCode: "",
    productType: "",
    productLine: "",
    warehouseName: "",
  };
}

export function billingClassificationRowsFromApi(row, assignedCompanyNumbers = []) {
  const fromList = Array.isArray(row?.billingDataClassifications)
    ? row.billingDataClassifications
    : [];
  if (fromList.length > 0) {
    return fromList.map((entry, index) => {
      const classification = String(entry.billingDataClassification ?? "").trim();
      const value = String(entry.billingDataClassificationValue ?? "").trim();
      const companyNumber = String(entry.companyNumber ?? "").trim();
      return {
        id: `bc-saved-${index}-${classification}`,
        companyNumber:
          companyNumber || (assignedCompanyNumbers.length === 1 ? assignedCompanyNumbers[0] : ""),
        billingDataClassification: classification,
        ...hydrateClassifyFieldsFromValue(classification, value),
      };
    });
  }
  const classification = String(row?.billingDataClassification ?? "").trim();
  const value = String(row?.billingDataClassificationValue ?? "").trim();
  if (!classification && !value) {
    return [];
  }
  return [
    {
      id: `bc-legacy-${row?.invProfileDetailId ?? row?.id ?? "0"}`,
      companyNumber: assignedCompanyNumbers.length === 1 ? assignedCompanyNumbers[0] : "",
      billingDataClassification: classification,
      ...hydrateClassifyFieldsFromValue(classification, value),
    },
  ];
}

export function ensureBillingClassificationRows(service, assignedCompanyNumbers = []) {
  const rows = service?.billingClassificationRows;
  if (Array.isArray(rows) && rows.length > 0) {
    return rows;
  }
  const fromApi = billingClassificationRowsFromApi(service, assignedCompanyNumbers);
  if (fromApi.length > 0) {
    return fromApi;
  }
  return [createEmptyBillingClassificationRow(assignedCompanyNumbers)];
}

export function syncLegacyBillingFieldsFromRows(service) {
  const rows = service?.billingClassificationRows ?? [];
  const first = rows[0];
  if (!first) {
    return {
      ...service,
      billingDataClassification: "",
      billingDataClassificationValue: "",
      productClass: "",
      productCode: "",
      productType: "",
      productLine: "",
      warehouseName: "",
      companyNumber: service?.companyNumber ?? "",
    };
  }
  const value = resolveRowBillingDataClassificationAttributeValue(first, []);
  return {
    ...service,
    billingDataClassification: first.billingDataClassification,
    billingDataClassificationValue: value,
    productClass: first.productClass,
    productCode: first.productCode,
    productType: first.productType,
    productLine: first.productLine,
    warehouseName: first.warehouseName,
    companyNumber: first.companyNumber,
  };
}

export function resolveRowBillingDataClassificationAttributeValue(row, productDivisions = []) {
  const direct = String(row?.billingDataClassificationValue ?? "").trim();
  if (direct) {
    return direct;
  }
  const classification = String(row?.billingDataClassification ?? "").trim();
  switch (classification) {
    case BILLING_DATA_CLASSIFICATION_PRODUCT_DIVISION: {
      const divisions = (productDivisions ?? [])
        .map((d) => String(d ?? "").trim())
        .filter(Boolean);
      return divisions.length > 0 ? divisions.join(", ") : "";
    }
    case BILLING_DATA_CLASSIFICATION_PRODUCT_CLASS:
      return String(row?.productClass ?? "").trim();
    case BILLING_DATA_CLASSIFICATION_PRODUCT_CODE:
      return String(row?.productCode ?? "").trim();
    case BILLING_DATA_CLASSIFICATION_PRODUCT_TYPE:
      return String(row?.productType ?? "").trim();
    case BILLING_DATA_CLASSIFICATION_PRODUCTION_LINE:
      return String(row?.productLine ?? "").trim();
    case BILLING_DATA_CLASSIFICATION_WAREHOUSE:
      return String(row?.warehouseName ?? "").trim();
    default:
      return "";
  }
}

export function buildBillingClassificationsSaveBody(rows, productDivisionsByRowId = {}) {
  return (rows ?? [])
    .map((row) => ({
      companyNumber: String(row.companyNumber ?? "").trim(),
      billingDataClassification: String(row.billingDataClassification ?? "").trim(),
      billingDataClassificationValue: resolveRowBillingDataClassificationAttributeValue(
        row,
        productDivisionsByRowId[row.id] ?? []
      ),
    }))
    .filter(
      (entry) => entry.billingDataClassification || entry.billingDataClassificationValue
    );
}

const BILLING_CLASSIFICATION_DUPLICATE_MESSAGE =
  "This billing data classification combination is already used.";

function defaultBillingClassificationCompanyNumber(assignedCompanyNumbers) {
  const assigned = (assignedCompanyNumbers ?? []).filter(Boolean);
  return assigned.length === 1 ? String(assigned[0] ?? "").trim() : "";
}

function billingClassificationValueFieldKey() {
  return "billingDataClassificationValue";
}

function resolvedBillingClassificationRowKey(
  row,
  assignedCompanyNumbers = [],
  productDivisions = []
) {
  const companyNumber =
    String(row?.companyNumber ?? "").trim() ||
    defaultBillingClassificationCompanyNumber(assignedCompanyNumbers);
  const classification = String(row?.billingDataClassification ?? "").trim();
  const value = resolveRowBillingDataClassificationAttributeValue(row, productDivisions);
  if (!classification || !value) {
    return "";
  }
  return `${companyNumber}\0${classification}\0${value}`;
}

function applyBillingClassificationDuplicateErrors(
  rowErrors,
  rows,
  assignedCompanyNumbers = [],
  productDivisionsByRowId = {}
) {
  const list = Array.isArray(rows) ? rows : [];
  const errors = (rowErrors ?? []).map((err) => (err ? { ...err } : null));
  const assigned = (assignedCompanyNumbers ?? []).filter(Boolean);
  const keyToIndexes = new Map();

  list.forEach((row, index) => {
    const key = resolvedBillingClassificationRowKey(
      row,
      assignedCompanyNumbers,
      productDivisionsByRowId[row.id] ?? []
    );
    if (!key) {
      return;
    }
    const indexes = keyToIndexes.get(key) ?? [];
    indexes.push(index);
    keyToIndexes.set(key, indexes);
  });

  for (const indexes of keyToIndexes.values()) {
    if (indexes.length <= 1) {
      continue;
    }
    for (const index of indexes) {
      const valueField = billingClassificationValueFieldKey();
      const existing = errors[index] ?? {};
      errors[index] = {
        ...existing,
        billingDataClassification: BILLING_CLASSIFICATION_DUPLICATE_MESSAGE,
        ...(valueField ? { [valueField]: BILLING_CLASSIFICATION_DUPLICATE_MESSAGE } : {}),
        ...(assigned.length > 1 ? { companyNumber: BILLING_CLASSIFICATION_DUPLICATE_MESSAGE } : {}),
      };
    }
  }

  return errors;
}

export function validateSingleBillingClassificationRowErrors(
  row,
  lookup = {},
  assignedCompanyNumbers = []
) {
  const err = {};
  const assigned = (assignedCompanyNumbers ?? []).filter(Boolean);
  if (assigned.length > 1 && !String(row?.companyNumber ?? "").trim()) {
    err.companyNumber = "Company number is required.";
  }
  const classification = String(row?.billingDataClassification ?? "").trim();
  if (!classification) {
    err.billingDataClassification = "Billing data classification is required.";
    return err;
  }

  const requireSelectField = (fieldKey, value, label, state = {}) => {
    const { loading = false, isError = false, optionsLength = 0 } = state;
    if (loading) {
      err[fieldKey] = `${label} is still loading.`;
      return;
    }
    if (isError) {
      err[fieldKey] = `Unable to load ${label.toLowerCase()} options.`;
      return;
    }
    if (optionsLength === 0) {
      err[fieldKey] = BILLING_DATA_CLASSIFICATION_NO_DATA_MESSAGE;
      return;
    }
    if (!String(value ?? "").trim()) {
      err[fieldKey] = `${label} is required.`;
    }
  };

  requireSelectField(
    "billingDataClassificationValue",
    row?.billingDataClassificationValue,
    "Billing report classify",
    lookup.billingReportClassify ?? {}
  );

  return Object.keys(err).length > 0 ? err : null;
}

/** True when the row has all required fields and lookup data for its classification type. */
export function isBillingClassificationRowComplete(
  row,
  lookup = {},
  assignedCompanyNumbers = []
) {
  return validateSingleBillingClassificationRowErrors(row, lookup, assignedCompanyNumbers) == null;
}

export function validateBillingClassificationRows(
  rows,
  assignedCompanyNumbers,
  lookupByRowId = {},
  productDivisionsByRowId = {}
) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) {
    return [{ _row: "Add at least one billing data classification." }];
  }
  const rowErrors = applyBillingClassificationDuplicateErrors(
    list.map((row) =>
      validateSingleBillingClassificationRowErrors(
        row,
        lookupByRowId[row.id] ?? {},
        assignedCompanyNumbers
      )
    ),
    list,
    assignedCompanyNumbers,
    productDivisionsByRowId
  );
  return rowErrors.some(Boolean) ? rowErrors : null;
}

export function serviceHasBillingClassificationRows(service) {
  const rows = service?.billingClassificationRows ?? [];
  if (rows.some((r) => String(r?.billingDataClassification ?? "").trim())) {
    return true;
  }
  return Boolean(
    String(service?.billingDataClassification ?? "").trim() ||
      String(service?.billingDataClassificationValue ?? "").trim()
  );
}
